import { BaseInput } from "shared/components/forms/BaseInput";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { formatPhone } from "shared/utils";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { ColorPicker } from "./components/ColorPicker";
import { LogoDropZone } from "./components/LogoDropZone";
import { getPropertyPresigned } from "../AddProperty/untils";
import { generatePrimaryPalette } from "./components/ColorPicker/untils";
import { uploadFileToS3Bucket, trimImageWhitespace } from "utils/fileHandlers";
import { SectionCard } from "shared/app/SectionCard";
import { RegisterTyped } from "shared/hooks/use-form-typed";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { checkImageQuality } from "./components/LogoDropZone/utils";

interface BrandingLandlordPmsValues {
  landlord_name?: string;
  landlord_phone?: string;
  landlord_email?: string;
}

export interface BrandingFormValues {
  enabled?: boolean;
  logo?: string;
  primary_color?: string;
  landlord_name?: string;
  landlord_phone?: string;
  landlord_email?: string;
  palette?: Record<string, string>;
  landlord_name_synced?: boolean;
  landlord_phone_synced?: boolean;
  landlord_email_synced?: boolean;
}

interface BrandingFieldsProps {
  register: RegisterTyped<BrandingFormValues>;
  sessionKey: string;
  showLandlordFields?: boolean;
  pmsValues?: BrandingLandlordPmsValues;
  onUploadingChange?: (isUploading: boolean) => void;
  onLogoUploaded?: (hasNewUpload: boolean) => void;
}

const NON_DIGITS = /\D/g;

type LandlordValueKey = "landlord_name" | "landlord_phone" | "landlord_email";
type LandlordSyncedKey =
  | "landlord_name_synced"
  | "landlord_phone_synced"
  | "landlord_email_synced";

const LandlordPmsField = ({
  register,
  valueKey,
  syncedKey,
  pmsValue,
  pmsDisplayValue,
  children
}: {
  register: RegisterTyped<BrandingFormValues>;
  valueKey: LandlordValueKey;
  syncedKey: LandlordSyncedKey;
  pmsValue?: string;
  pmsDisplayValue?: string;
  children: (readOnly: boolean) => React.ReactNode;
}) => {
  const syncedField = register(syncedKey);
  const isPmsOwned = Boolean(pmsDisplayValue);
  const synced = isPmsOwned && syncedField.value !== false;

  if (!isPmsOwned) return <div>{children(false)}</div>;

  return (
    <div>
      {children(synced)}
      <PmsSyncOption
        name={`${valueKey}-pms-sync`}
        onChange={(nextSynced) => {
          syncedField.onChange(nextSynced);
          if (nextSynced) register(valueKey).onChange(pmsValue);
        }}
        value={synced}
      />
      <PMSValue value={pmsDisplayValue} />
    </div>
  );
};

export const BrandingFields = ({
  register,
  sessionKey,
  showLandlordFields = false,
  pmsValues,
  onUploadingChange,
  onLogoUploaded
}: BrandingFieldsProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    register("logo").value
  );

  const { mutate: handleLogoUpload, isPending } = useMutation({
    mutationFn: async (file: File) => {
      const quality = await checkImageQuality(file);
      if (!quality.ok) toast.warn(quality.reason);

      const trimmedFile = await trimImageWhitespace(file);
      const presigned = await getPropertyPresigned(trimmedFile, sessionKey);
      await uploadFileToS3Bucket(presigned.response.url, trimmedFile);
      return {
        globalId: presigned.asset.global_id as string,
        file: trimmedFile
      };
    },
    onMutate: () => onUploadingChange?.(true),
    onSuccess: ({ globalId, file }) => {
      register("logo").onChange(globalId);
      onLogoUploaded?.(true);
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => onUploadingChange?.(false)
  });

  const handleLogoSelect = (file: File | null) => {
    if (!file) {
      register("logo").onChange(undefined);
      setPreviewUrl(undefined);
      onLogoUploaded?.(false);
      return;
    }
    handleLogoUpload(file);
  };

  const handleColorChange = (color: string) => {
    const palette = generatePrimaryPalette(color);
    register("primary_color").onChange(palette[500]);
    register("palette").onChange(palette);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6 items-center">
        <LogoDropZone
          onFileSelect={handleLogoSelect}
          logoUrl={previewUrl}
          isProcessing={isPending}
          processingLabel={isPending ? "Uploading..." : undefined}
        />
        <div>
          <ColorPicker
            value={register("primary_color").value || ""}
            onChange={handleColorChange}
            label="Primary color"
            placeholder="#E6007E"
            defaultValue="#E6007E"
            error={register("primary_color").error}
          />
        </div>
      </div>

      {showLandlordFields && (
        <div className="grid grid-cols-2 gap-6">
          <LandlordPmsField
            pmsDisplayValue={pmsValues?.landlord_name}
            pmsValue={pmsValues?.landlord_name}
            register={register}
            syncedKey="landlord_name_synced"
            valueKey="landlord_name"
          >
            {(readOnly) => (
              <BaseInput
                id="landlord-name"
                label="Landlord name"
                placeholder="John Doe"
                maxLength={100}
                readOnly={readOnly}
                {...register("landlord_name")}
              />
            )}
          </LandlordPmsField>
          <LandlordPmsField
            pmsDisplayValue={
              pmsValues?.landlord_phone
                ? formatPhone(pmsValues.landlord_phone)
                : undefined
            }
            pmsValue={pmsValues?.landlord_phone?.replace(NON_DIGITS, "")}
            register={register}
            syncedKey="landlord_phone_synced"
            valueKey="landlord_phone"
          >
            {(readOnly) => (
              <BaseInput
                id="landlord-phone"
                label="Landlord phone"
                placeholder="(555) 123-4567"
                parser={tenDigitTelParser({ asString: true })}
                value={register("landlord_phone").value ?? ""}
                onChange={register("landlord_phone").onChange}
                error={register("landlord_phone").error}
                readOnly={readOnly}
              />
            )}
          </LandlordPmsField>
          <LandlordPmsField
            pmsDisplayValue={pmsValues?.landlord_email}
            pmsValue={pmsValues?.landlord_email}
            register={register}
            syncedKey="landlord_email_synced"
            valueKey="landlord_email"
          >
            {(readOnly) => (
              <BaseInput
                id="landlord-email"
                label="Landlord email"
                placeholder="landlord@example.com"
                type="email"
                readOnly={readOnly}
                {...register("landlord_email")}
              />
            )}
          </LandlordPmsField>
        </div>
      )}
    </div>
  );
};

interface BrandingFormProps {
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
  register: RegisterTyped<{
    enabled?: boolean;
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    palette?: Record<string, string>;
  }>;
  sessionKey: string;
  onUploadingChange?: (isUploading: boolean) => void;
  showLandlordFields?: boolean;
}

export const BrandingForm = ({
  register,
  sessionKey,
  headerExtra,
  footer,
  onUploadingChange,
  showLandlordFields = true
}: BrandingFormProps) => {
  return (
    <SectionCard title="Branding" headerExtra={headerExtra} footer={footer}>
      <BrandingFields
        register={register}
        sessionKey={sessionKey}
        showLandlordFields={showLandlordFields}
        onUploadingChange={onUploadingChange}
      />
    </SectionCard>
  );
};
